'use client'

import { CheckCircle2Icon, TriangleAlertIcon, XCircleIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { CustomIcon } from '@/components/icons'
import { toAccountStatus } from '@/lib/utils/exchange-util'
import { PublicAccount } from '@/types/generated/common/account'

interface Props {
  account: PublicAccount
  exact?: boolean
}

export function AccountStatusChecker({ account, exact = false }: Props) {
  const t = useTranslations('settings')
  const status = toAccountStatus(account)
  const StatusIcon = useMemo(() => {
    if (exact) {
      if (status.isActive) {
        return <CheckCircle2Icon className="text-chart-mint size-3.5" />
      } else {
        return <XCircleIcon className="text-chart-red size-3.5" />
      }
    } else {
      switch (status.statusType) {
        case 'active':
          return <CheckCircle2Icon className="text-chart-mint size-3.5" />
        case 'inactive':
          return <XCircleIcon className="text-chart-red size-3.5" />
        case 'suspended':
          return <TriangleAlertIcon className="text-ox-gold size-3.5" />
        case 'creating':
        case 'auth_failed':
        case 'pending_auth':
          return <CustomIcon.AccountLoader className="size-3.5 animate-spin" />
      }
    }
  }, [status.statusType, exact, status.isActive])

  return (
    <div className="flex items-center gap-2">
      <p className="text-sm font-medium tracking-tight text-white/60">
        {exact
          ? t(`accountStatus.${status.statusType}`)
          : status.isActive
            ? t('accountStatus.active')
            : t('accountStatus.inactive')}
      </p>
      {StatusIcon}
    </div>
  )
}
